import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface Appointment {
  id: string;
  conversation_id: string;
  annonce_id: string | null;
  client_user_id: string;
  artisan_user_id: string;
  status: string;
  starts_at: string;
  duration_minutes: number;
  location_type: string;
  location_text: string;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  expires_at: string;
}

export interface AppointmentFormData {
  startsAt: string;
  startTime: string;
  durationMinutes: number;
  locationType: "clientAddress" | "call" | "other";
  locationText: string;
  notes: string;
}

export const useAppointments = (conversationId: string | null) => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);

  const loadAppointments = useCallback(async () => {
    if (!conversationId || !user) return;
    const { data } = await supabase
      .from("appointments")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });
    if (data) {
      // Client-side expiration check
      const now = new Date();
      const updated = (data as Appointment[]).map((apt) => {
        if (
          (apt.status === "proposed" || apt.status === "counterProposed") &&
          new Date(apt.expires_at) < now
        ) {
          // Fire and forget update
          supabase
            .from("appointments")
            .update({ status: "expired" })
            .eq("id", apt.id)
            .then();
          return { ...apt, status: "expired" };
        }
        return apt;
      });
      setAppointments(updated);
    }
  }, [conversationId, user]);

  useEffect(() => {
    loadAppointments();
    if (!conversationId) return;
    const channel = supabase
      .channel(`appointments-${conversationId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "appointments", filter: `conversation_id=eq.${conversationId}` },
        () => loadAppointments()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [conversationId, loadAppointments]);

  const getConversationData = async () => {
    if (!conversationId) return null;
    const { data } = await supabase
      .from("conversations")
      .select("client_id, artisan_id, annonce_id")
      .eq("id", conversationId)
      .maybeSingle();
    return data;
  };

  const createAppointment = async (form: AppointmentFormData) => {
    if (!conversationId || !user) return null;
    setLoading(true);
    try {
      const convo = await getConversationData();
      if (!convo) throw new Error("Conversation introuvable");

      const startsAt = new Date(`${form.startsAt}T${form.startTime}`).toISOString();
      const isArtisan = user.id === convo.artisan_id;

      const { data, error } = await supabase.from("appointments").insert({
        conversation_id: conversationId,
        annonce_id: convo.annonce_id,
        client_user_id: convo.client_id,
        artisan_user_id: convo.artisan_id,
        starts_at: startsAt,
        duration_minutes: form.durationMinutes,
        location_type: form.locationType,
        location_text: form.locationText,
        notes: form.notes || null,
        created_by: isArtisan ? "artisan" : "client",
        status: "proposed",
      } as any).select().single();

      if (error) throw error;

      // Send system message
      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: `📅 Proposition de rendez-vous`,
        message_type: "appointment",
        attachments: [{ type: "appointment", appointmentId: (data as any).id }],
      } as any);

      toast({ title: "Proposition envoyée", description: "Le rendez-vous a été proposé." });
      return data;
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message || "Impossible de proposer le RDV", variant: "destructive" });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (
    appointmentId: string,
    newStatus: string,
    reason?: string
  ) => {
    if (!user) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from("appointments")
        .update({ status: newStatus, notes: reason || undefined } as any)
        .eq("id", appointmentId);
      if (error) throw error;

      const statusLabels: Record<string, string> = {
        accepted: "✅ Rendez-vous accepté",
        confirmed: "✅ Rendez-vous confirmé",
        declined: "❌ Rendez-vous refusé",
        canceled: "🚫 Rendez-vous annulé",
      };

      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: statusLabels[newStatus] || `Rendez-vous mis à jour (${newStatus})`,
        message_type: "appointment",
        attachments: [{ type: "appointment_update", appointmentId, status: newStatus }],
      } as any);

      const isAccepted = newStatus === "accepted" || newStatus === "confirmed";
      toast({ title: "Mis à jour", description: `Le rendez-vous a été ${isAccepted ? "accepté" : newStatus === "declined" ? "refusé" : "annulé"}.` });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const counterPropose = async (appointmentId: string, form: AppointmentFormData) => {
    if (!user || !conversationId) return;
    setLoading(true);
    try {
      const startsAt = new Date(`${form.startsAt}T${form.startTime}`).toISOString();
      const { error } = await supabase
        .from("appointments")
        .update({
          status: "counterProposed",
          starts_at: startsAt,
          duration_minutes: form.durationMinutes,
          location_type: form.locationType,
          location_text: form.locationText,
          notes: form.notes || null,
          expires_at: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        } as any)
        .eq("id", appointmentId);
      if (error) throw error;

      await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: "🔁 Contre-proposition de rendez-vous",
        message_type: "appointment",
        attachments: [{ type: "appointment_update", appointmentId, status: "counterProposed" }],
      } as any);

      toast({ title: "Contre-proposition envoyée" });
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getActiveAppointment = () => {
    return appointments.find(
      (a) => !["declined", "canceled", "expired"].includes(a.status)
    );
  };

  return {
    appointments,
    loading,
    createAppointment,
    updateAppointmentStatus,
    counterPropose,
    getActiveAppointment,
    loadAppointments,
  };
};

// Generate Google Calendar link (Option A - no OAuth)
export const generateGoogleCalendarUrl = (apt: Appointment): string => {
  const start = new Date(apt.starts_at);
  const end = new Date(start.getTime() + apt.duration_minutes * 60000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const locationLabel =
    apt.location_type === "call" ? "Appel / Visio" : apt.location_text;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `Rendez-vous - RDV-${apt.id.slice(0, 4).toUpperCase()}`,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: apt.notes || "Rendez-vous via Hirafi",
    location: locationLabel,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Generate ICS file content
export const generateICS = (apt: Appointment): string => {
  const start = new Date(apt.starts_at);
  const end = new Date(start.getTime() + apt.duration_minutes * 60000);
  const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const locationLabel =
    apt.location_type === "call" ? "Appel / Visio" : apt.location_text;

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Hirafi//RDV//FR",
    "BEGIN:VEVENT",
    `UID:${apt.id}@hirfati`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:Rendez-vous - RDV-${apt.id.slice(0, 4).toUpperCase()}`,
    `LOCATION:${locationLabel}`,
    `DESCRIPTION:${apt.notes || "Rendez-vous via Hirafi"}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
};

export const downloadICS = (apt: Appointment) => {
  const content = generateICS(apt);
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `rdv-${apt.id.slice(0, 4)}.ics`;
  a.click();
  URL.revokeObjectURL(url);
};
